/**
 * URL → PanelStack mapping for the AppShell.
 *
 * The React Router URL is the canonical source of truth for which panels are
 * mounted. `pathToStack` is a pure function so the AppShell can `useMemo` it
 * against `pathname + search`; any URL change re-runs this and the controlled
 * `<PanelStack>` will push/pop panels accordingly.
 *
 * Stack order is bottom-to-top — the LAST entry is the active (visible) panel.
 *
 * TWO APPS. This dispatched twenty-eight surfaces; the product is the Control
 * Panel and Files, and the rest are in `.archive/routes/` — built, working, and
 * set aside rather than deleted, because a working page removed from the tree is
 * a decision nobody can review later. Bringing one back is three edits: move the
 * file, add its branch here, add its entry to the app catalog.
 *
 * Search params deliberately do NOT differentiate panel identity — the panel
 * reads `useSearchParams` itself.
 */

import type { AppPanel } from "./types";
import { ControlPanel } from "../../routes/control-panel";
import { Dashboard } from "../../routes/dashboard";
import { Files } from "../../routes/files";
import { Profile } from "../../routes/profile";
import { Settings } from "../../routes/settings";

export function pathToStack(pathname: string, _search: string): readonly AppPanel[] {
  const dashboard: AppPanel = {
    renderPanel: Dashboard,
    props: {},
    title: "Workspace",
    htmlTitle: "Workspace",
  };

  if (/^\/workspace\/?$/.test(pathname)) {
    return [{ ...dashboard, title: "Home", htmlTitle: "Workspace" }];
  }

  if (pathname === "/" || pathname === "") {
    return [dashboard];
  }

  // ── Files ─────────────────────────────────────────────────────────────────
  //
  // Compass owns its subroute grammar below one workspace application root.
  // `/files` remains a compatibility alias; emitted links use Compass paths.
  const compassMatch = pathname.match(/^\/workspace\/compass(?:\/view\/([^/]+)|\/[^/]*)?\/?$/);
  const filesMatch = compassMatch ?? pathname.match(/^\/files(?:\/([^/]+))?\/?$/);
  if (filesMatch) {
    const files: AppPanel = {
      renderPanel: Files,
      props: filesMatch[1] ? { spaceId: filesMatch[1] } : {},
      title: "Files",
      htmlTitle: "Files",
    };
    return [dashboard, files];
  }

  // ── Control Panel ─────────────────────────────────────────────────────────
  //
  // Bare `/workspace/control-panel/` is the application document. Foundry's
  // canonical client routes then carry scope, RID and sub-route; only a scoped
  // sub-route enters the settings module.
  // Routing the bare path into the module is what made the landing page a roles
  // table.
  const foundryControlPanelMatch = pathname.match(
    /^\/workspace\/control-panel\/(?:enrollment|organization|space)\/[^/]+\/([^/]+)(?:\/.*)?\/?$/,
  );
  const controlPanelRoot = /^\/workspace\/control-panel\/?$/.test(pathname);
  // Keep the former route as a compatibility alias while every emitted link
  // uses the Foundry contract.
  const legacyControlPanelMatch = pathname.match(/^\/control-panel(?:\/([^/]+))?\/?$/);
  const controlPanelMatch = foundryControlPanelMatch ?? legacyControlPanelMatch;
  if (controlPanelRoot || controlPanelMatch) {
    const sub = controlPanelMatch?.[1];
    const controlPanel: AppPanel = sub && sub !== "home"
      ? { renderPanel: Settings, props: { sub }, title: "Control Panel", htmlTitle: "Control Panel" }
      : { renderPanel: ControlPanel, props: {}, title: "Control Panel", htmlTitle: "Control Panel" };
    return [dashboard, controlPanel];
  }

  /*
    `/settings` still resolves. It is in links, in tests and in muscle memory,
    and breaking a working URL to tidy a name is a cost paid by users for
    nothing. All settings is the landing page, as it is upstream.
  */
  const settingsMatch = pathname.match(/^\/settings(?:\/([^/]+))?\/?$/);
  if (settingsMatch) {
    const settings: AppPanel = {
      renderPanel: Settings,
      props: { sub: settingsMatch[1] ?? "all-settings" },
      title: "Control Panel",
      htmlTitle: "Control Panel",
    };
    return [dashboard, settings];
  }

  // ── Profile ───────────────────────────────────────────────────────────────
  //
  // Not an app: reached from the user menu, the way the tenant reaches its own.
  // The launcher lists products, and your own account is not one.
  if (/^\/profile\/?$/.test(pathname)) {
    const profile: AppPanel = {
      renderPanel: Profile,
      props: {},
      title: "Profile",
      htmlTitle: "Profile",
    };
    return [dashboard, profile];
  }

  // Unknown path → fall back to Dashboard so the user always sees something.
  return [dashboard];
}
